import type { IconName as VendoredIconName } from "components/ui";
import type { IconName } from "./registry";

/**
 * The icon set is mid-transition, on purpose. See DESIGN_SYSTEM.md §6,
 * "Migration status".
 *
 * Phase 4 swapped every `<Icon>` call site onto Lucide in one commit. Choosing
 * the *right* glyph for a button is a per-button decision, though, and doing 71
 * of them in an afternoon produces icons that are merely different rather than
 * better — a wrong icon quietly misleads people for years, where a mismatched
 * stroke weight only looks untidy. So the old glyphs come back until each one
 * has been chosen deliberately.
 *
 * Every name below renders from the vendored `components/ui` set. Nothing else
 * changes: call sites keep importing `Icon` from `components/hds`, keep the same
 * `name`, and keep working when a name graduates.
 *
 * **To move one icon onto the new set: delete its line.** That is the whole
 * procedure. The list can only shrink, which is what makes the transition
 * finishable — and `SharedIconName` means a typo or a name that exists in only
 * one of the two sets is a compile error, not a blank square.
 */
type SharedIconName = IconName & VendoredIconName;

const legacyNames: readonly SharedIconName[] = [
	/* --- navigation and chrome --- */
	"ArrowBottomIcon",
	"ArrowTopIcon",
	"ArrowLeftIcon",
	"ArrowRightIcon",
	"ArrowBottom2Icon",
	"ArrowTop2Icon",
	/* ArrowDownFilledIcon is deliberately NOT here — see §6's migration note.
	   The top-bar switcher chip's chevron geometry was tuned to the Lucide glyph;
	   the old one is a filled triangle with a 12px natural size and would need
	   that tuning redone. Add the line when you want it back. */
	"CloseIcon",
	"EllipsisIcon",
	"EllipsisVerticalIcon",
	"SearchIcon",
	"FilterIcon",
	"FolderIcon",
	"SettingIcon",

	/* --- actions --- */
	"PlusIcon",
	"CopyIcon",
	"DeleteIcon",
	"SaveIcon",
	"SaveAsIcon",
	"RefreshIcon",
	"RestoreIcon",
	"PencilIcon",
	"ShareIcon",
	"EmbedIcon",
	"SwitchIcon",
	"DocumentIcon",

	/* --- feedback --- */
	"CorrectIcon",
	"ExclamationIcon",
	"WarningIcon",
	"QuestionIcon",
	"FullFavouriteIcon",

	/* --- identity and access --- */
	"UserIcon",
	"AddUserIcon",
	"OrganizationIcon",
	"LockIcon",
	"KeyIcon",

	/* --- 3D editor tools --- */
	"MoveIcon",
	"RotateIcon",
	"ScaleIcon",
	"DistanceIcon",
	"CrossSectionIcon",
	"WalkingIcon",
	"CameraIcon",
	"Camera2Icon",
	"MapIcon",
	"PrototypeIcon",

	/* --- library taxonomy: rooms --- */
	"BathroomIcon",
	"KitchenIcon",
	"LivingIcon",
	"SleepingIcon",
	"DiningIcon",
	"ChildrenIcon",
	"WorkingIcon",
	"CorridorIcon",
	"OutdoorIcon",

	/* --- library taxonomy: building elements --- */
	"FacadeIcon",
	"RoofIcon",
	"TechnicalIcon",
	"DeviceIcon",
	"LampIcon",
	"EnvironmentIcon",

	/* --- library taxonomy: materials --- */
	"WoodIcon",
	"StoneIcon",
	"TilesIcon",
	"CarpetIcon",
	"TextilesIcon",
	"WallpaperIcon",
	"GlassIcon",
	"ColorfulIcon",
	"PlantsIcon",
	"AccessoiresIcon",
];

const legacySet: ReadonlySet<string> = new Set(legacyNames);

/**
 * Whether this name still renders from the vendored set. `set="hds"` at a call
 * site overrides it — used where a surface is new enough that the old set has no
 * glyph for half of it, and mixing the two inside one panel would look worse
 * than either.
 */
export const rendersFromVendoredSet = (name: IconName): boolean => legacySet.has(name);

/** Names still on the old set, for the /dev/design reference page. */
export const legacyIconNames = legacyNames;
